// src/modules/academy/hooks/useAcademyData.ts

import { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../../shared/context/AppContext'; // ← CAMBIADO
import { userService } from '../../../modules/users/services/userService';
import { UserProfile } from '../../../types/user';
import { AcademyUser, UserRole } from '../types';

interface AcademyDataState {
  academyUser: AcademyUser | null;
  profile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  dataSource: 'database' | 'simulated' | 'mixed';
}

interface UseAcademyDataReturn extends AcademyDataState {
  refreshData: () => Promise<void>;
  clearError: () => void;
}

export const useAcademyData = (): UseAcademyDataReturn => {
  const { state: appState } = useAppContext();
  const authState = appState.auth;
  
  const [dataState, setDataState] = useState<AcademyDataState>({
    academyUser: null,
    profile: null,
    isLoading: true,
    error: null,
    dataSource: 'simulated'
  });

  // Función para determinar roles basados en participaciones
  const extractRolesFromParticipations = (participations: any[]): UserRole[] => {
    const roles: Set<UserRole> = new Set();
    
    participations.forEach(participation => {
      const role = participation.role as UserRole;
      if (['participant', 'speaker', 'judge'].includes(role)) {
        roles.add(role);
      }
    });

    // Si no hay roles específicos, asignar participant por defecto
    if (roles.size === 0) {
      roles.add('participant');
    }

    return Array.from(roles);
  };

  // Función para convertir User + UserProfile a AcademyUser
  const createAcademyUser = useCallback((profile: UserProfile | null): AcademyUser | null => {
    if (!authState.user) return null;

    let userRoles: UserRole[] = ['participant']; // Valor por defecto
    let dataSource: 'database' | 'simulated' | 'mixed' = 'simulated';

    if (profile?.participations && profile.participations.length > 0) {
      userRoles = extractRolesFromParticipations(profile.participations);
      dataSource = 'database';
    }

    return {
      id: authState.user.id,
      email: authState.user.email,
      name: authState.user.name,
      picture: authState.user.picture,
      roles: userRoles,
      // Campos que vendrán de la base de datos en futuras iteraciones
      bio: undefined,
      specializations: undefined,
      experience_level: 'intermediate',
      institution: undefined,
      country: undefined,
      is_active: authState.user.is_active,
      created_at: authState.user.created_at,
      last_login: authState.user.last_login
    };
  }, [authState.user]);

  // Función para cargar datos del perfil
  const loadProfileData = useCallback(async () => {
    if (!authState.isAuthenticated || !authState.token) {
      setDataState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Usuario no autenticado'
      }));
      return;
    }

    try {
      setDataState(prev => ({ ...prev, isLoading: true, error: null }));
      
      console.log('📄 Cargando datos del perfil desde la base de datos...');
      const profileResponse = await userService.getUserProfile();

      if (profileResponse.success && profileResponse.data) {
        console.log('✅ Datos del perfil cargados exitosamente:', {
          participations: profileResponse.data.participations?.length || 0,
          certificates: profileResponse.data.certificates?.length || 0,
          statistics: profileResponse.data.statistics
        });

        const academyUser = createAcademyUser(profileResponse.data);
        
        // Determinar fuente de datos
        let dataSource: 'database' | 'simulated' | 'mixed' = 'simulated';
        const hasParticipations = (profileResponse.data.participations?.length ?? 0) > 0;
        const hasCertificates = (profileResponse.data.certificates?.length ?? 0) > 0;
        const hasStatistics = (profileResponse.data.statistics?.total_events_participated ?? 0) > 0;
        
        if (hasParticipations || hasCertificates || hasStatistics) {
          dataSource = 'database';
        }

        setDataState({
          academyUser,
          profile: profileResponse.data,
          isLoading: false,
          error: null,
          dataSource
        });
      } else {
        console.warn('⚠️ No se pudieron cargar datos del perfil, usando datos simulados');
        const academyUser = createAcademyUser(null);
        
        setDataState({
          academyUser,
          profile: null,
          isLoading: false,
          error: null,
          dataSource: 'simulated'
        });
      }
    } catch (error: any) {
      console.error('❌ Error cargando datos del perfil:', error);
      
      // En caso de error, crear usuario con datos básicos
      const academyUser = createAcademyUser(null);
      
      setDataState({
        academyUser,
        profile: null,
        isLoading: false,
        error: error.message || 'Error cargando datos del usuario',
        dataSource: 'simulated'
      });
    }
  }, [authState.isAuthenticated, authState.token, createAcademyUser]);

  // Función para refrescar datos
  const refreshData = useCallback(async () => {
    await loadProfileData();
  }, [loadProfileData]);

  // Función para limpiar errores
  const clearError = useCallback(() => {
    setDataState(prev => ({ ...prev, error: null }));
  }, []);

  // Cargar datos al montar o cuando cambie la autenticación
  useEffect(() => {
    loadProfileData();
  }, [loadProfileData]);

  // Log de cambios para debugging
  useEffect(() => {
    if (dataState.academyUser) {
      console.log('📊 Estado de datos de academia actualizado:', {
        user: dataState.academyUser.email,
        roles: dataState.academyUser.roles,
        dataSource: dataState.dataSource,
        hasProfile: !!dataState.profile,
        hasParticipations: dataState.profile?.participations?.length || 0,
        hasStatistics: !!dataState.profile?.statistics
      });
    }
  }, [dataState]);

  return {
    ...dataState,
    refreshData,
    clearError
  };
};